import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { GoogleMapApiConfig } from '../config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from '../mapConfig';
import { FONT_FAMILY } from '../common/sharedFunctions';
import { colors } from './Theme/WebTheme';
import { PICKUP_PIN_COLOR, DROP_PIN_COLOR } from './BookingMapPicker';

// Statuses during which the assigned driver's phone pushes positions into tracking/<bookingId>.
export const ACTIVE_STATUSES = ['ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED'];
export const STALE_AFTER_MS = 60000;

export const isTrackable = (booking) =>
  Boolean(booking && booking.driver && ACTIVE_STATUSES.indexOf(booking.status) >= 0);

// Language key describing the phase of the trip.
export const trackingPhaseKey = (status) =>
  status === 'ACCEPTED' ? 'driver_on_the_way'
    : status === 'ARRIVED' ? 'driver_arrived'
      : status === 'STARTED' ? 'trip_in_progress'
        : status === 'REACHED' ? 'trip_reached'
          : null;

// Number(null) is 0, so missing values must become NaN explicitly.
const asNumber = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));

const toLatLng = (p) => {
  const lat = asNumber(p ? p.lat : null), lng = asNumber(p ? p.lng : null);
  return isFinite(lat) && isFinite(lng) ? { lat: lat, lng: lng } : null;
};

// Where the driver is heading: the pickup before the trip starts, the drop-off afterwards.
export const trackingTarget = (booking) => {
  if (!booking) return null;
  const toPickup = booking.status === 'ACCEPTED' || booking.status === 'ARRIVED';
  return toLatLng(toPickup ? booking.pickup : booking.drop);
};

export const pointIsStale = (point, now) => {
  const at = asNumber(point ? point.at : null);
  return !isFinite(at) || now - at > STALE_AFTER_MS;
};

const PIN_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
const pinIcon = (color) => ({
  path: PIN_PATH, fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1.5, scale: 1.1,
  anchor: new window.google.maps.Point(0, 0), labelOrigin: new window.google.maps.Point(0, -29),
});
const carIcon = (image) => image
  ? { url: image, scaledSize: new window.google.maps.Size(40, 28), anchor: new window.google.maps.Point(20, 14) }
  : { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: colors.Action_Button_Back, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 };

const PICKUP_LABEL = { text: 'P', color: '#ffffff', fontFamily: FONT_FAMILY };
const DROP_LABEL = { text: 'D', color: '#ffffff', fontFamily: FONT_FAMILY };
const MAP_OPTIONS = { streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false, gestureHandling: 'cooperative' };
const containerStyle = { height: '360px', width: '100%', borderRadius: 8, fontFamily: FONT_FAMILY };
const DEFAULT_CENTER = { lat: 14.5995, lng: 120.9842 };
const MAX_FIT_ZOOM = 17;

// Live map of the assigned driver for one active booking. Subscribes to the last point of
// tracking/<bookingId> through the shared action (same source the mobile app uses).
export default function DriverTrackingMap(props) {
  const { booking } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const coords = useSelector((state) => state.locationdata.coords);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries: GoogleMapApiOptions.libraries,
  });
  const [map, setMap] = useState(null);
  const [follow, setFollow] = useState(true);
  const [fitted, setFitted] = useState(false);
  const [now, setNow] = useState(Date.now());
  const bookingId = booking && booking.id;
  // The map's `center` prop must keep one identity: a fresh object every render would re-centre on
  // every tick/update and undo fitBounds, follow-off and the user's panning. Movement is done by effects.
  const initialCenter = useRef(null);

  useEffect(() => {
    if (!bookingId) return undefined;
    dispatch(api.fetchBookingLocations(bookingId));
    const tick = setInterval(() => setNow(Date.now()), 10000);
    return () => {
      clearInterval(tick);
      dispatch(api.stopLocationFetch(bookingId));
    };
  }, [bookingId, dispatch]);

  const car = toLatLng(coords);
  const pickup = toLatLng(booking && booking.pickup);
  const drop = toLatLng(booking && booking.drop);
  if (!initialCenter.current && (car || pickup || drop)) {
    initialCenter.current = car || pickup || drop;
  }
  const target = trackingTarget(booking);
  const stale = pointIsStale(coords, now);

  const icons = useMemo(() => (isLoaded ? {
    pickup: pinIcon(PICKUP_PIN_COLOR),
    drop: pinIcon(DROP_PIN_COLOR),
    car: carIcon(booking && booking.car_image),
  } : null), [isLoaded, booking && booking.car_image]); // eslint-disable-line react-hooks/exhaustive-deps

  // First fix: fit everything; afterwards keep the car in view while "follow" is on.
  useEffect(() => {
    if (!map || !window.google) return;
    if (!fitted && (car || pickup || drop)) {
      const bounds = new window.google.maps.LatLngBounds();
      [car, pickup, drop].forEach((p) => { if (p) bounds.extend(p); });
      map.fitBounds(bounds, 60);
      // A single point (or car already at the pickup) would zoom to the maximum.
      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      });
      setFitted(true);
    } else if (follow && car) {
      map.panTo(car);
    }
  }, [map, car && car.lat, car && car.lng, follow]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoaded || !icons) {
    return null;
  }

  const phaseKey = trackingPhaseKey(booking && booking.status);
  const statusText = !car
    ? t('waiting_for_driver_location')
    : (phaseKey ? t(phaseKey) : '') + (stale ? ' · ' + t('driver_location_stale') : '');

  return (
    <div style={{ fontFamily: FONT_FAMILY }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Typography variant="body1" style={{ flex: '1 1 200px', fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
          {statusText}
        </Typography>
        <Button size="small" variant={follow ? 'contained' : 'outlined'} color="primary" onClick={() => setFollow(!follow)} style={{ fontFamily: FONT_FAMILY }}>
          {t('follow_driver')}
        </Button>
      </div>
      <GoogleMap
        zoom={14}
        center={initialCenter.current || DEFAULT_CENTER}
        mapContainerStyle={containerStyle}
        options={MAP_OPTIONS}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onDragStart={() => setFollow(false)}
      >
        {pickup ? <Marker position={pickup} icon={icons.pickup} label={PICKUP_LABEL} title={t('pickup_location')} /> : null}
        {drop ? <Marker position={drop} icon={icons.drop} label={DROP_LABEL} title={t('drop_location')} /> : null}
        {car ? <Marker position={car} icon={icons.car} title={booking.driver_name || ''} zIndex={10} /> : null}
        {car && target ? (
          <Polyline path={[car, target]} options={{ strokeColor: colors.Action_Button_Back, strokeOpacity: 0.8, strokeWeight: 3, geodesic: true }} />
        ) : null}
      </GoogleMap>
    </div>
  );
}